import { useState } from 'react';
import { TableRow, TableCell, IconButton } from '@material-ui/core';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';

import { useStoreActions } from '~/store/hooks';
import { isoToFormat } from '~/utils/date';
import { extractErrorMessage } from '~/utils/error';
import { getEditValues } from '../form';
import ConfirmModal from '~/ui/components/common/ConfirmModal';
import GoalModal from '../../popups/GoalModal';

import { Timezone } from '~/types';
import { ICarePlanGoal } from '~/services/api/carePlanGoals/types';
import { IFormValues } from '../../popups/GoalModal/form/types';
import { IPagePermissions } from '../../../types';
import styles from './Item.module.scss';
import ReadMoreLessHtml from '~/ui/components/common/ReadMoreLessHtml';

interface IProps {
  item: ICarePlanGoal;
  permissions: IPagePermissions;
  onCarePlanChange: () => void;
}

const Item = ({ item, permissions, onCarePlanChange }: IProps) => {
  const [showEdit, setShowEdit] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const { onUpdateCarePlanGoal, onRemoveCarePlanGoal } = useStoreActions(
    actions => actions.carePlanGoals,
  );
  const { showError } = useStoreActions(actions => actions.snackbar);

  const handleUpdateGoal = async (values: IFormValues) => {
    const payload = {
      ...values,
      id: item.id,
      date: isoToFormat(values.date, 'y-MM-dd'),
    };

    try {
      setIsUpdating(true);
      await onUpdateCarePlanGoal(payload);
      onCarePlanChange();
      setShowEdit(false);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveGoal = async () => {
    try {
      setIsRemoving(true);
      await onRemoveCarePlanGoal(item.id);
      onCarePlanChange();
    } catch (e) {
      showError(extractErrorMessage(e));
      setIsRemoving(false);
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>{isoToFormat(item.date, 'dd MMM y', Timezone.GMT, false)}</TableCell>
        <TableCell>
          <ReadMoreLessHtml html={item.reasonHtml} />
        </TableCell>
        {permissions.canManage && (
          <TableCell align="right">
            <IconButton
              className={styles.iconButton}
              title="Edit"
              onClick={() => setShowEdit(true)}
            >
              <CreateIcon className={styles.icon} />
            </IconButton>
            <IconButton
              className={styles.iconButton}
              title="Remove"
              onClick={() => setShowRemove(true)}
            >
              <DeleteIcon className={styles.icon} />
            </IconButton>
          </TableCell>
        )}
      </TableRow>
      {showEdit && (
        <GoalModal
          mode="edit"
          initialValues={getEditValues(item)}
          isProcessing={isUpdating}
          onSubmit={handleUpdateGoal}
          onClose={() => setShowEdit(false)}
        />
      )}
      {showRemove && (
        <ConfirmModal
          isLoading={isRemoving}
          title="Remove Goal"
          description="Are you sure you want to remove current Goal?"
          onClose={() => setShowRemove(false)}
          onConfirm={handleRemoveGoal}
        />
      )}
    </>
  );
};

export default Item;
